import type { Localized } from '../../i18n'
import { employmentError } from './employmentError'

type Tr = { ru: string; az: string; en: string }

/** Кириллица или азербайджанские буквы — значит, текст писал человек (бэк или его AZ-перевод). */
const HUMAN = /[А-Яа-яЁёƏəĞğİıÖöŞşÇçÜü]/
/** Машинный код перед человеческим текстом: «SOME_CODE: Текст» → «Текст». */
const CODE_PREFIX = /^[A-Z][A-Z0-9_]{3,}:\s*/
/** Сервер не ответил: web «Failed to fetch» / Firefox «NetworkError…» / Safari «Load failed» / RN «Network request failed». */
const NETWORK_RE = /failed to fetch|network|load failed/i

const GENERIC: Tr = {
  ru: 'Не удалось выполнить действие. Повторите попытку.',
  az: 'Əməliyyat yerinə yetirilmədi. Yenidən cəhd edin.',
  en: 'The action failed. Please retry.',
}
const NETWORK: Tr = {
  ru: 'Нет связи с сервером. Проверьте интернет или VPN и повторите.',
  az: 'Serverlə əlaqə yoxdur. İnterneti və ya VPN-i yoxlayıb yenidən cəhd edin.',
  en: 'No connection to the server. Check your internet or VPN and retry.',
}
const REJECTED: Tr = {
  ru: 'Сервер не принял данные. Проверьте поля; если ошибка повторяется — обновите страницу.',
  az: 'Server məlumatları qəbul etmədi. Sahələri yoxlayın; təkrarlanarsa, səhifəni yeniləyin.',
  en: 'The server rejected the data. Check the fields; if it repeats, reload the page.',
}
const UNAVAILABLE: Tr = {
  ru: 'Сервер или 1С временно недоступны. Повторите через минуту.',
  az: 'Server və ya 1C müvəqqəti əlçatan deyil. Bir dəqiqədən sonra təkrar edin.',
  en: 'The server or 1C is temporarily unavailable. Retry in a minute.',
}
const SERVER: Tr = {
  ru: 'Ошибка на сервере. Повторите позже; если не проходит — сообщите в поддержку.',
  az: 'Serverdə xəta baş verdi. Sonra təkrar edin; alınmasa, dəstəyə yazın.',
  en: 'Server error. Retry later; if it persists, contact support.',
}
const BY_STATUS: Record<number, Tr> = {
  401: { ru: 'Сессия истекла. Войдите снова.', az: 'Sessiyanın vaxtı bitib. Yenidən daxil olun.', en: 'Your session has expired. Sign in again.' },
  403: { ru: 'Недостаточно прав для этого действия.', az: 'Bu əməliyyat üçün icazəniz yoxdur.', en: 'You do not have permission for this action.' },
  404: { ru: 'Не найдено: данные могли измениться. Обновите страницу.', az: 'Tapılmadı: məlumatlar dəyişmiş ola bilər. Səhifəni yeniləyin.', en: 'Not found: the data may have changed. Reload the page.' },
  409: { ru: 'Данные уже изменились. Обновите страницу и повторите.', az: 'Məlumatlar artıq dəyişib. Səhifəni yeniləyib təkrar edin.', en: 'The data has changed. Reload the page and retry.' },
  413: { ru: 'Файл слишком большой.', az: 'Fayl çox böyükdür.', en: 'The file is too large.' },
  429: { ru: 'Слишком много запросов. Подождите минуту и повторите.', az: 'Çox sayda sorğu. Bir dəqiqə gözləyib təkrar edin.', en: 'Too many requests. Wait a minute and retry.' },
  502: UNAVAILABLE,
  503: UNAVAILABLE,
  504: UNAVAILABLE,
}

const withTail = (m: Tr, tail: string): Tr => {
  const s = tail.length > 160 ? `${tail.slice(0, 159)}…` : tail
  return { ru: `${m.ru} [${s}]`, az: `${m.az} [${s}]`, en: `${m.en} [${s}]` }
}

/**
 * Что показать пользователю вместо сырой ошибки запроса (для `t()`).
 * Коды мест работы — через employmentError, без внутренних деталей. Человеческий текст бэка —
 * как есть. Техническое (английский валидатор, «Forbidden resource», страница nginx, «Failed to
 * fetch») — фраза по ситуации + хвост `[HTTP … · исходное]`, чтобы по скриншоту было видно, что
 * случилось. Ответ сервера узнаём по числовому `status` (ApiError), а не по instanceof: тесты
 * компонентов мокают api/client одним `api`, и класса ApiError там нет.
 */
export function errorText(err: unknown): Localized {
  const raw = err instanceof Error ? err.message.trim() : ''
  const known = employmentError(raw)
  if (known) return known
  const text = raw.replace(CODE_PREFIX, '')
  if (HUMAN.test(text)) return text
  const status = (err as { status?: unknown } | null | undefined)?.status
  if (typeof status === 'number') {
    const tail = !raw ? `HTTP ${status}` : raw.startsWith(String(status)) ? `HTTP ${raw}` : `HTTP ${status} · ${raw}`
    return withTail(BY_STATUS[status] ?? (status >= 500 ? SERVER : REJECTED), tail)
  }
  if (!raw) return GENERIC
  const network = NETWORK_RE.test(raw) || (err as Error).name === 'AbortError'
  return withTail(network ? NETWORK : GENERIC, raw)
}
